/**
 * ContactDetailsTab — Tab 1 of the patient-edit shell (PROD-728).
 *
 * Mirrors Flutter `contact_details_widget.dart`. Composes:
 *   - `ContactDetailsForm` — ownership, email, primary/secondary phones,
 *     preferred-contact star.
 *   - `AddressList` — residential addresses (multiple, with one default).
 *
 * Save flow:
 *   - Calls `useUpdatePatient` → PUT /api/Patients/{id} with the full
 *     patient body. The hook strips emergency contacts + insurance
 *     (their own endpoints) and renames `patientMrn` → `patientMRN` on
 *     send. The contact tab folds `updatePatientContactRequest` into the
 *     patient body — Flutter parity. Earlier this tab POSTed directly
 *     to `CreateUpdatePerson`, which is reserved for the inline
 *     create-appointment patient-create flow only (hotfix shipped in
 *     PR #108).
 *   - On success: toast + invalidate the patient query so the shell
 *     refetches and the form rehydrates.
 *
 * Permission gate: `Patient_Contacts` (Flutter `:840`) — visibility
 * already enforced at the tab-strip level via `usePermission` in
 * `patient-edit-page.tsx`. Save further requires `edit: true`.
 *
 * Read-only mode when `patient.isMerged === true` — Flutter renders the
 * tab inside an `AbsorbPointer`. We disable each control via the
 * `readOnly` prop instead so a screen-reader can still announce values.
 */

import { useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { serverErrorMessage } from '@/shared/lib/toast-helpers';
import { usePermission } from '@/auth/permission-gate';
import { useAuthStore } from '@/auth/auth-store';
import { SessionOrgPending } from '@/patients/tabs/session-org-pending';
import { FormSection } from '@/patients/components/personal-details/form-primitives';
import { ContactDetailsForm } from '@/patients/components/contact-details/contact-details-form';
import { AddressList } from '@/patients/components/contact-details/address-list';
import { HealthInsuranceSection } from '@/patients/components/health-insurance/health-insurance-section';
import { CorporateAgreementsSection } from '@/patients/components/corporate-agreements/corporate-agreements-section';
import {
  contactDetailsSchema,
  defaultContactValues,
  PREFERRED_PRIMARY,
  type ContactDetailsFormValues,
} from '@/patients/components/contact-details/contact-details-schema';
import { usePatientEditShell } from '@/patients/components/patient-edit-shell-context';
import { useUpdatePatient } from '@/patients/services/use-update-patient';
import type {
  Patient,
  PatientAddress,
  UpdatePatientContactRequest,
  CorporateList,
} from '@/types/patient-api';

export interface ContactDetailsTabProps {
  patient: Patient | null;
  /**
   * Fired AFTER a successful save. Lets an embedding surface refresh its own
   * data (e.g. the appointment panel invalidates appointment-details). Optional
   * + default undefined → no behaviour change for the patient-edit page.
   */
  onSaved?: () => void;
}

/**
 * PROD-2959: the organization is the patient's own, else the signed-in user's — never a hard-coded DEV
 * organization. Until one is known the tab renders `SessionOrgPending`, not the form.
 */
export function ContactDetailsTab(props: ContactDetailsTabProps) {
  const auth = useAuthStore((s) => s.user);
  const orgId = props.patient?.organizationId ?? auth?.organizationId;
  if (!orgId) return <SessionOrgPending userLoaded={!!auth} />;
  return <ContactDetailsTabBody {...props} orgId={orgId} />;
}

function ContactDetailsTabBody({ patient, onSaved, orgId }: ContactDetailsTabProps & { orgId: string }) {
  const canEdit = usePermission(['Patient_Contacts'], { edit: true });
  const readOnly = !canEdit || patient?.isMerged === true;

  const initialValues = useMemo<ContactDetailsFormValues>(
    () => patientToContactValues(patient),
    [patient?.patientId],
  );

  const form = useForm<ContactDetailsFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(contactDetailsSchema),
    mode: 'onChange',
  });
  const { reset, formState, handleSubmit } = form;
  const { isDirty, isValid, isSubmitting } = formState;
  // A save succeeded in this visit and nothing has been edited since: the shell shows "Changes saved" instead of Save.
  const [savedOnce, setSavedOnce] = useState(false);
  // The last save's failure, shown as the form action bar's error status until the next attempt (same words as the toast).
  const [saveError, setSaveError] = useState<string | null>(null);
  useEffect(() => {
    if (isDirty) setSavedOnce(false);
  }, [isDirty]);

  // Re-seed when the patient query settles.
  useEffect(() => {
    if (patient?.patientId) {
      reset(patientToContactValues(patient));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.patientId]);

  // PR #108 hotfix: use the corrected `useUpdatePatient` (PUT
  // /api/Patients/{id}) rather than POSTing directly to CreateUpdatePerson.
  // The hook owns the patientMRN-rename, sub-resource strips, and address
  // contactId cleanup. We just hand it the merged Patient.
  const saveMutation = useUpdatePatient();

  const onSubmit = handleSubmit(async (values) => {
    const patientId = patient?.patientId;
    if (!patientId) {
      toast.error('Cannot save — patient id missing.');
      return;
    }
    try {
      await saveMutation.mutateAsync({
        patientId,
        patient: mergePatientWithContactValues(patient, values),
      });
      toast.success('Patient details saved');
      reset(values);
      setSavedOnce(true);
      onSaved?.();
    } catch (err) {
      // No global MutationCache.onError exists in this app: without this the failure was silent.
      toast.error("Couldn't save patient details", { description: serverErrorMessage(err) });
      setSaveError("Couldn't save patient details");
    }
  }, (errors) => {
    const first = Object.values(errors as Record<string, { message?: string } | undefined>).find((e) => e?.message);
    toast.error("Couldn't save patient details", { description: first?.message ?? 'Check the highlighted fields.' });
    setSaveError("Couldn't save patient details");
  });

  // Register save action with the shell — same pattern as Personal Details.
  const { setSaveAction, setIsDirty } = usePatientEditShell();
  useEffect(() => {
    setSaveAction({
      label: 'Save',
      dirty: isDirty,
      saved: savedOnce,
      onClick: () => {
        setSaveError(null);
        void onSubmit();
      },
      disabled: readOnly || !isValid || !isDirty || isSubmitting || saveMutation.isPending,
      busy: isSubmitting || saveMutation.isPending,
      errorText: saveError,
    });
    return () => setSaveAction(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, isValid, isDirty, isSubmitting, saveMutation.isPending, savedOnce, saveError]);

  useEffect(() => {
    setIsDirty(isDirty);
    return () => setIsDirty(false);
  }, [isDirty, setIsDirty]);

  const isAdult = patient?.isAdult ?? true;

  return (
    <FormProvider {...form}>
      <form className="max-w-3xl pb-32">
        <FormSection title="Contact details">
          <ContactDetailsForm isAdult={isAdult} readOnly={readOnly} />
        </FormSection>
        <FormSection title="Residential address">
          <AddressList readOnly={readOnly} />
        </FormSection>

        {/* Health Insurance + Corporate Agreement — Flutter renders both on
            the Contact Details tab (`contact_details_widget.dart:105`,`:114`).
            Each is self-contained (own list + Save via its own endpoint) and
            renders a "save the patient first" hint until a patientId exists. */}
        <FormSection title="Health insurance">
          <HealthInsuranceSection
            patientId={patient?.patientId ?? null}
            organizationId={orgId}
            readOnly={readOnly}
          />
        </FormSection>
        <FormSection title="Corporate agreement">
          <CorporateAgreementsSection
            patientId={patient?.patientId ?? null}
            organizationId={orgId}
            initialList={(patient?.corporateAgreementResponse ?? []).filter(
              (c): c is CorporateList => c != null,
            )}
            readOnly={readOnly}
          />
        </FormSection>
      </form>
    </FormProvider>
  );
}

// ── Projection helpers ────────────────────────────────────────────────────

// Exported for the chart Demog tab (PROD-1988), which re-hosts these same forms.
export function patientToContactValues(patient: Patient | null): ContactDetailsFormValues {
  if (!patient) return defaultContactValues(null);
  const src = patient.updatePatientContactRequest;
  return {
    patientId: patient.patientId ?? null,
    ownership: src?.ownership ?? '',
    email: src?.email ?? patient.email ?? '',
    primaryContact: src?.primaryContact ?? patient.phoneNumber ?? '',
    secondayContact: src?.secondayContact ?? null,
    isPreferrable:
      src?.isPreferrable === 0 || src?.isPreferrable === 1 || src?.isPreferrable === 2
        ? src.isPreferrable
        : PREFERRED_PRIMARY,
    addresses: (src?.patientAddress ?? []).map((a) => ({
      contactId: a?.contactId ?? null,
      locationName: a?.locationName ?? '',
      building: a?.building ?? null,
      apartment: a?.apartment ?? null,
      address: a?.address ?? null,
      city: a?.city ?? null,
      country: a?.country ?? null,
      latitude: a?.latitude ?? null,
      longitude: a?.longitude ?? null,
      notes: a?.notes ?? null,
      isDefault: a?.isDefault ?? false,
      isActive: a?.isActive ?? true,
      isDeleted: a?.isDeleted ?? false,
      isEdit: false,
      isManualAddress: false,
    })),
  };
}

/**
 * Merge the contact-details form values into the existing `Patient` so
 * the mutation hook can ship it as the PUT body. The hook
 * (`useUpdatePatient.toWireBody`) owns the strips/renames; this helper
 * just builds the merged in-memory `Patient`.
 *
 * Only the `updatePatientContactRequest` sub-object is authoritative
 * from this tab. All other patient fields carry through from `existing`
 * untouched — the PUT body shape (verified via Flutter curl 2026-05-14)
 * includes the full patient body even when only contact details changed.
 *
 * UI-only address flags (`isEdit`, `isManualAddress`) are stripped before
 * wire mapping.
 */
// Exported for the chart Demog tab (PROD-1988), which re-hosts these same forms.
export function mergePatientWithContactValues(
  existing: Patient | null,
  values: ContactDetailsFormValues,
): Patient {
  const wireAddresses: PatientAddress[] = values.addresses.map((row) => ({
    contactId: row.contactId,
    locationName: row.locationName,
    apartment: row.apartment,
    notes: row.notes,
    city: row.city,
    country: row.country,
    latitude: row.latitude,
    longitude: row.longitude,
    building: row.building,
    address: row.address,
    isDeleted: row.isDeleted,
    isDefault: row.isDefault,
    isActive: row.isActive,
  }));

  const contactRequest: UpdatePatientContactRequest = {
    patientId: existing?.patientId ?? null,
    ownership: values.ownership,
    email: values.email,
    primaryContact: values.primaryContact,
    secondayContact: values.secondayContact,
    isPreferrable: values.isPreferrable,
    isContactEdit: true,
    isAddressEdit: true,
    patientAddress: wireAddresses,
  };

  // Spread `existing` first so all read-only carry-through fields
  // (consent, age, timeZone, nationality, identification, etc.) survive.
  // Then overlay the only sub-object this tab authoritatively owns.
  return {
    ...(existing ?? ({} as Patient)),
    updatePatientContactRequest: contactRequest,
  } as Patient;
}
